import React, { useEffect, useRef } from 'react';
import useAppDownloadModal from '../utils/app-download-hooks';
import { installAdjustAnchorPageTagging } from '../utils/adjust-page-tag';

// Matches the app-download landing route only (with or without a locale prefix and
// trailing slash): /app-download, /app-download/, /id/app-download, /en/app-download/.
// Sub-pages like /app-download/weekly-brief/ are intentionally NOT matched.
const APP_DOWNLOAD_PATH = /^(\/[a-z]{2})?\/app-download\/?$/;

/**
 * App-wide interceptor: any in-app click on a link pointing to the /app-download
 * landing route opens the "Get the app" banner modal (or deep-links to the store on
 * mobile) instead of navigating to the page. Direct visits, QR codes and modified
 * clicks (new tab / cmd+click) still reach the page normally.
 */
export default function AppDownloadModalProvider({ children }) {
  const { openDownloadModal, downloadModal } = useAppDownloadModal();
  const openRef = useRef(openDownloadModal);
  openRef.current = openDownloadModal;

  useEffect(() => {
    const handleClick = (event) => {
      // Leave modified / non-primary clicks alone (new tab, download, middle click).
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      const anchor = event.target.closest && event.target.closest('a[href]');
      if (!anchor) {
        return;
      }

      const target = anchor.getAttribute('target');
      if (target && target !== '_self') {
        return;
      }

      let pathname;
      try {
        pathname = new URL(anchor.href, window.location.origin).pathname;
      } catch (error) {
        return;
      }

      if (!APP_DOWNLOAD_PATH.test(pathname)) {
        return;
      }

      // Capture phase + stopPropagation prevents Gatsby's Link from navigating.
      event.preventDefault();
      event.stopPropagation();
      // SEOENG-56: an <a href="/app-download"> anywhere on the site is an acquisition CTA; a
      // data-cta-location on the anchor names it, else it reports as the generic link.
      openRef.current({ ctaLocation: anchor.dataset.ctaLocation || 'app_download_link' });
    };

    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, []);

  // SEOENG-117: plain <a> links to an Adjust tracker (the footer store badges, links in CMS copy)
  // carry the page as the Adjust adgroup by the time the browser follows them.
  useEffect(() => installAdjustAnchorPageTagging(document), []);

  return (
    <>
      {children}
      {downloadModal}
    </>
  );
}
